import { QueryClient } from "@tanstack/react-query"
import { isAxiosError } from "axios"

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      refetchOnMount: false,
      staleTime: 2 * 60 * 1000,
      gcTime: 5 * 60 * 1000,
      retry: (failureCount, error: any) => {
        if (isAxiosError(error)) {
          const status = error.response?.status
          if (status && status >= 400 && status < 500) {
            return false
          }
        }
        return failureCount < 3
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 30000),
      networkMode: "online"
    },
    mutations: {
      retry: (failureCount, error: any) => {
        if (isAxiosError(error)) {
          const status = error.response?.status
          if (status && status >= 400 && status < 500) {
            return false
          }
        }
        return failureCount < 3
      }
    }
  }
})

const inactivityTimers = new Map<string, NodeJS.Timeout>()

queryClient.getQueryCache().subscribe((event) => {
  const { query, type } = event as { type: string; query: any }

  const keyHash = query.queryHash

  if (type === "removed") {
    // Clear any lingering timer if already removed
    inactivityTimers.delete(keyHash)
    return
  }

  if (!query.isActive()) {
    // Schedule removal after 60s of inactivity
    if (!inactivityTimers.has(keyHash)) {
      const timer = setTimeout(() => {
        if (!query.isActive()) {
          queryClient.removeQueries({ queryKey: query.queryKey, exact: true })
        }
        inactivityTimers.delete(keyHash)
      }, 60_000)
      inactivityTimers.set(keyHash, timer)
    }
  } else {
    // Cancel pending removal if query became active again
    const timer = inactivityTimers.get(keyHash)
    if (timer) {
      clearTimeout(timer)
      inactivityTimers.delete(keyHash)
    }
  }
})
